<script setup lang="ts">
import { MapPinned } from '@lucide/vue'

import PoiCategoryBrowser from '@/components/map/PoiCategoryBrowser.vue'
import AnimatedXButton from '@/components/ui/AnimatedXButton.vue'
import { Z_INDEX } from '@/utils/zIndex'

/**
 * Desktop floating "Map content" panel (a docked card) — the user-facing home for POI categories you
 * can show on the map and add to the open collection. Mobile uses MapToolSheet (a non-modal bottom
 * sheet) instead; the category browser itself is the shared PoiCategoryBrowser. Chrome mirrors the
 * Map layers panel (white surface, rounded-2xl, title + X). The developer-oriented "Map layers" panel
 * is separate.
 */
defineProps<{ isVisible: boolean }>()
const emit = defineEmits<{ 'toggle-visibility': [] }>()
const closePanel = () => emit('toggle-visibility')
</script>

<template>
  <div
    v-if="isVisible"
    class="absolute bottom-24 right-2 flex max-h-112 w-72 flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_14px_30px_rgba(15,23,42,0.16)]"
    :style="{ zIndex: Z_INDEX.MAP_LAYERS_PANEL }"
    data-testid="map-content-panel"
  >
    <div class="flex shrink-0 items-center justify-between border-b border-gray-100 px-3 py-2.5">
      <div class="flex items-center gap-2">
        <MapPinned class="h-4 w-4 text-gray-600" />
        <span class="text-sm font-semibold tracking-tight text-gray-900">Map content</span>
      </div>
      <AnimatedXButton size="sm" @click="closePanel" />
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto">
      <PoiCategoryBrowser />
    </div>
  </div>
</template>
